'use client';

import styles from './read-more-wrapper.module.scss';
import {
	type CSSProperties,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useTranslations } from 'next-intl';
import useIsHydrated from 'hooks/use-is-hydrated';
import { ToggleButton } from 'components/controls/buttons/toggle-button';

const DEFAULT_LINE_HEIGHT = 20 * 1.75; //Default size * css line-height.

const calculateHeights = (element: HTMLDivElement | null, maxLines: number) => {
	//Calculate lineHeight and scrollheight(which includes overflow)
	//Get the real line height of the component by reading calculated CSS
	const lineHeight = element
		? parseInt(getComputedStyle(element).lineHeight)
		: 0;
	const scrollHeight = element?.scrollHeight;
	const collapsedHeight = lineHeight * maxLines;
	return [lineHeight, scrollHeight, collapsedHeight];
};

const ReadMoreWrapper = ({ children, maxLines = 8 }: IProps) => {
	const t = useTranslations('components');
	const wrapper = useRef<HTMLDivElement>(null);
	const isHydrated = useIsHydrated();
	const [hasTransition, setHasTransition] = useState(false); //Only use a transition once a user interacts with the View More button.
	const [lineHeight, setLineHeight] = useState(DEFAULT_LINE_HEIGHT); //Default of our small text size with our normal line-height.
	const [isExpanded, setIsExpanded] = useState(false);
	const [scrollHeight, setScrollHeight] = useState(null); //Component max height (including overflow)
	const [shouldTruncate, setShouldTruncate] = useState(true); //Is the max element height lower than the truncation height.

	// The height at which the component's height should be collapsed.
	const collapsedHeight = lineHeight * maxLines;
	// The current actual height of the component
	const height = isExpanded ? scrollHeight : collapsedHeight;

	useEffect(() => {
		const calculateHeightsAfterRender = () => {
			const [newLineHeight, newScrollHeight, newCollapsedHeight] =
				calculateHeights(wrapper.current, maxLines);

			setLineHeight(newLineHeight);
			setScrollHeight(newScrollHeight);
			//If pressing Read More would only show <2 lines, just render the {children} without a readMore.
			setShouldTruncate(
				newCollapsedHeight + newLineHeight * 2 < (newScrollHeight || 0),
			);
		};

		calculateHeightsAfterRender();
		const handleResize = () => calculateHeightsAfterRender();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [maxLines]);

	//Toggle between collapsed and open
	const handleclick = () => {
		setHasTransition(true);
		if (!isExpanded) {
			setIsExpanded(true);
		} else {
			setIsExpanded(false);
			wrapper.current.scrollIntoView({ block: `nearest` });
		}
	};

	return (
		<div>
			<div
				style={
					{
						'--max-wrapper-height': height ? `${height}px` : '100%',
					} as CSSProperties
				}
				className={`
				${shouldTruncate ? styles.wrapper : ''}
				${hasTransition ? styles.hasTransition : ''}
				${!isExpanded && shouldTruncate ? styles.isCollapsed : ''}`}
				ref={wrapper}
				data-testid="read-more-content"
			>
				{children}
			</div>
			{isHydrated && shouldTruncate && (
				<ToggleButton
					style={{ marginTop: 'var(--spacing-micro)' }}
					tabIndex={0}
					isChevronUp={isExpanded}
					onClick={handleclick}
					data-testid="read-more-toggle"
					text={!isExpanded ? t('viewMore') : t('viewLess')}
				/>
			)}
		</div>
	);
};

interface IProps {
	children: ReactNode;
	maxLines?: number;
}

export default ReadMoreWrapper;
